<template>
  <RfBaseModal
    ref="modalRef"
    title="Edit tag"
    width="459"
    secondaryText="Submit"
    closeText="Cancel"
    @close="closeEditTagModal"
    @secondary="emit('edit', tagRef) && closeEditTagModal()"
  >
    <template #body>
      <div class="flex flex-col">
        <RfTextInput
          v-model.trim="tagRef.title"
          density="compact"
          variant="outlined"
          persistentHint
          class="pb-1"
          placeholder="Tag 1"
          counter="15"
          maxlength="15"
          title="Name"
          :rules="[value => !!value || 'Name field is required.']"
        ></RfTextInput>
        <RfTextInput
          v-model.trim="tagRef.description"
          density="compact"
          variant="outlined"
          persistentHint
          data-cy="element-info--name"
          placeholder="A tag for..."
          title="Description (optional)"
          class="pb-1"
        ></RfTextInput>
        <div class="flex flex-col gap-1">
          <div>Color</div>
          <div class="flex gap-1">
            <RfColorTag
              v-for="color in tagsColors"
              :key="color"
              :backgroundColor="color"
              class="h-10 w-10"
              :selected="getTagColor(tagRef.color) === color"
              @click="tagRef.color = color"
            />
          </div>
        </div>
        <div ref="freqCapFields" class="tag-freq-cap-div">
          <div class="mt-6 text-lg font-bold">Frequency Cap</div>
          <v-radio-group
            v-model="isCapped"
            class="mt-2 pt-0"
            @update:modelValue="updateFrequencyCap"
          >
            <v-radio :value="false" label="None" />
            <v-radio :value="true" label="Custom" />
          </v-radio-group>
          <div v-if="isCapped" class="pt-2">
            <div>
              Limit the number of prompts seen by a user.
              {{ freqCapHint }}
            </div>
            <div class="frequency-cap-val items-center gap-2">
              <RfSelect
                v-model="capType"
                variant="outlined"
                density="compact"
                :items="[VISIT, DAYS]"
                itemTitle="text"
                name="cap_type"
                hint="Select the type of limit"
                persistentHint
                bgColor="#fff"
                @update:modelValue="updateFrequencyCap"
              ></RfSelect>
            </div>
            <div class="frequency-cap-val">
              <RfTextInput
                id="limit-count"
                v-model.trim="tagRef.filter.frequency_cap.cap"
                variant="outlined"
                density="compact"
                title="Limit count"
                hint="Max number of prompts that can be shown to a user."
                persistentHint
              />
            </div>
            <div v-if="capType === DAYS.value" class="frequency-cap-val items-center gap-2">
              <RfSelect
                v-model="tagRef.filter.frequency_cap.cap_seconds"
                variant="outlined"
                density="compact"
                :items="capValues"
                title="Reset after"
                hint="Time period before cap is reset."
                persistentHint
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </RfBaseModal>
</template>

<script setup>
import RfSelect from "@/components/legacy/RfSelectLegacy.vue";
import RfTextInput from "@/components/legacy/RfInputLegacy.vue";
import RfBaseModal from "@/components/modals/RfBaseModal.vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";
import { ref, watch, computed } from "vue";
import { tagsColors, getTagColor } from "@/utils/constants/ColorsConstants";
import { VISIT, DAYS } from "@/utils/constants/FrequencyCapConstants";

const emit = defineEmits(["edit"]);

const props = defineProps({
  tag: { type: Object, default: () => ({}) },
  currApp: { type: Object, default: () => ({}) },
});

const modalRef = ref();
const freqCapFields = ref();
const tagRef = ref({ title: "", description: "", color: "", filter: {} });

const isCapped = ref(false);
const capType = ref();

const capValues = computed(() => {
  const maxDays = props.currApp.flags.extended_interactions_interval ? 365 : 30;
  return [
    ...[15, 30, 45].map(el => ({
      title: `${el} minutes`,
      value: el * 60,
    })),
    { title: "1 hour", value: 3600 },
    ...new Array(maxDays).fill().map((_, i) => ({
      title: `${i + 1} day${i > 0 ? "s" : ""}`,
      value: (i + 1) * 86400,
    })),
  ];
});

const freqCapHint = computed(() => {
  if (tagRef.value.filter?.frequency_cap?.cap_seconds === VISIT.value) {
    return "Specify the maximum number of prompts with this tag that should be shown to a user per visit before the limit is reset.";
  }
  return "Specify the maximum number of prompts with this tag that should be shown to a user and the number of days before the limit is reset.";
});

const updateFrequencyCap = () => {
  if (!isCapped.value) {
    tagRef.value.filter.frequency_cap = null;
  } else {
    tagRef.value.filter.frequency_cap = {
      is_capped: true,
      cap: tagRef.value.filter.frequency_cap?.cap,
      cap_seconds: tagRef.value.filter.frequency_cap?.cap_seconds,
    };
  }
};

const showEditTagModal = () => modalRef.value?.show();
const closeEditTagModal = () => emit("close");

watch(
  () => props.tag,
  newTag => {
    if (newTag) {
      tagRef.value = JSON.parse(JSON.stringify(newTag));
      tagRef.value.filter = tagRef.value.filter || {};
      tagRef.value.filter.frequency_cap = tagRef.value.filter.frequency_cap || {};
      isCapped.value = !!tagRef.value.filter.frequency_cap.is_capped;
      capType.value =
        tagRef.value.filter.frequency_cap.cap_seconds === VISIT.value ? VISIT.value : DAYS.value;
    }
  },
  { immediate: true },
);

defineExpose({ showEditTagModal, closeEditTagModal });
</script>
